/**
 * Fee / rounding-safe max amounts for sliders ("100%" must succeed on-chain).
 * Protocol uses e8 integers; UI floats + Math.round can hit the mint floor.
 */

const E8 = 1e8;

/** Round down to e8 precision (never round up into an illegal max). */
export function floorE8(amount: number): number {
	if (!(amount > 0) || !Number.isFinite(amount)) return 0;
	return Math.floor(amount * E8 + 1e-12) / E8;
}

/**
 * Haircut a theoretical max (mint capacity, etc.).
 * Default 99.5% covers CR equality, stability dust, float→nat rounding.
 */
export function haircutMax(amount: number, bps = 50): number {
	if (!(amount > 0)) return 0;
	const factor = Math.max(0, Math.min(10_000, 10_000 - bps)) / 10_000;
	return floorE8(amount * factor);
}

/** Default ICRC-1 transfer fee for 8-decimal demo ledgers (0.0001). */
export const DEFAULT_ICRC_FEE = 0.0001;

/**
 * Max token user can spend from wallet for a simple transfer (1 fee).
 * fee in token units (e.g. 0.0001 for e8 fee of 10_000).
 */
export function spendableTokenMax(
	walletBal: number,
	fee: number = DEFAULT_ICRC_FEE,
): number {
	if (!(walletBal > 0)) return 0;
	const f = fee > 0 ? fee : 0;
	return floorE8(Math.max(0, walletBal - f));
}

/**
 * Max for ICRC-2 deposit into vault: approve fee + transfer_from fee.
 * Without 2× fee, Max often fails with InsufficientFunds after approve.
 */
export function depositableTokenMax(
	walletBal: number,
	fee: number = DEFAULT_ICRC_FEE,
): number {
	if (!(walletBal > 0)) return 0;
	const f = fee > 0 ? fee : 0;
	return floorE8(Math.max(0, walletBal - 2 * f));
}

/**
 * Max USDM mintable after haircut.
 * theoretical = max(0, collUsd / minCr - debt)
 */
export function mintableUsdmMax(
	collateralUsd: number,
	debtUsdm: number,
	minCr: number,
	haircutBps = 50,
): number {
	if (!(collateralUsd > 0) || !(minCr > 0)) return 0;
	const theoretical = Math.max(0, collateralUsd / minCr - Math.max(0, debtUsdm));
	return haircutMax(theoretical, haircutBps);
}

/**
 * Max USDM repay = min(debt, depositable wallet USDM).
 * Repay uses ICRC-2 approve + transfer_from (2× fee), same as deposit.
 */
export function repayableUsdmMax(
	debtUsdm: number,
	walletUsdm: number,
	fee: number = DEFAULT_ICRC_FEE,
): number {
	const spendable = depositableTokenMax(walletUsdm, fee);
	if (!(debtUsdm > 0) || !(spendable > 0)) return 0;
	return floorE8(Math.min(debtUsdm, spendable));
}

/** Free collateral withdraw cap already computed; floor e8 only. */
export function withdrawableTokenMax(freeToken: number): number {
	return floorE8(Math.max(0, freeToken));
}
